import { isPlatformBrowser, Location } from '@angular/common';
import { inject, InjectionToken, Injector, PLATFORM_ID } from '@angular/core';
import { isActive, Router, ROUTES } from '@angular/router';
import { DevToolsRegistry } from '@core/frontend-shared/dev-tools';
import { parseUrlParams2 } from '@core/shared/utils';
import { RouterLinkRedirectType } from '../../feature';
import { RouterLinkRedirectsService } from '../../feature/router-link-redirects';
import { ROUTER_NAVIGATION_INTERCEPTOR } from '../navigation';
import * as i0 from "@angular/core";
export const CUSTOM_ROUTER_CONFIG = /*#__PURE__*/new InjectionToken('CUSTOM_ROUTER_CONFIG');
export let CustomRouter = /*#__PURE__*/(() => {
  class CustomRouter extends Router {
    get redirects() {
      if (!this._redirects) this._redirects = this.injector2.get(RouterLinkRedirectsService);
      return this._redirects;
    }
    constructor(injector2) {
      super();
      this.injector2 = injector2;
      // HACK: unlock some private props by aliasing
      this.location2 = inject(Location);
      this.stateManager2 = this.stateManager;
      this.navigationTransitions2 = this.navigationTransitions;
      this.navigationInterceptors = injector2.get(ROUTER_NAVIGATION_INTERCEPTOR, []);
      this.isBrowser = isPlatformBrowser(injector2.get(PLATFORM_ID));
      // this.trackAllNavigations();
      injector2.get(DevToolsRegistry).exposeDebugFunction(['Router', 'Routes'], () => {
        return injector2.get(ROUTES).reduce((all, next) => {
          return [...all, ...next];
        }, []);
      });
    }
    /**
     * NOTE: Navigations can be triggered by multiple ways. These are the possibilities:
     * - [boot] -> initialNavigation() -> navigateToSyncWithBrowser
     * - setUpLocationChangeListener() -> [browser location change] -> navigateToSyncWithBrowser
     * - [routerLinkDirective] -> navigateByUrl()
     * - [manual function call] -> navigate() -> navigateByUrl
     *
     * all of these must be overwritten to introduce fully customized behavior.
     * Which is pretty difficult because ng devs decided to keep much as private.
     */
    // modification: replace navigateToSyncWithBrowser by customNavigateToSyncWithBrowser
    initialNavigation() {
      this.setUpLocationChangeListener();
      if (!this.navigationTransitions2.hasRequestedNavigation) {
        const path = this.location2.path(true);
        this.customNavigateToSyncWithBrowser(path, 'imperative', this.stateManager2.restoredState());
      }
    }
    // modification: replace navigateToSyncWithBrowser by customNavigateToSyncWithBrowser
    setUpLocationChangeListener() {
      this.nonRouterCurrentEntryChangeSubscription ??= this.stateManager2.registerNonRouterCurrentEntryChangeListener((url, state) => {
        setTimeout(() => {
          this.customNavigateToSyncWithBrowser(url, 'popstate', state);
        }, 0);
      });
    }
    navigateByUrl(url, extras = {
      skipLocationChange: false
    }) {
      let urlTree = typeof url === 'string' ? this.parseUrl(url) : url;
      return this.waitForNavigationInterceptors(url, extras).then(continueNavigation => {
        if (!continueNavigation) {
          return false;
        } else {
          urlTree = this.maybeApplyRouterLinkRedirect(urlTree, extras);
          return super.navigateByUrl(urlTree, extras);
        }
      });
    }
    /**
     * modified version of navigateToSyncWithBrowser.
     * added maybeApplyRouterLinkRedirect to ensure correct redirects on initial navigation + location changes
     */
    customNavigateToSyncWithBrowser(url, source, state) {
      const extras = {
        replaceUrl: true
      };
      return this.waitForNavigationInterceptors(url, extras).then(continueNavigation => {
        if (!continueNavigation) {
          // TODO: problem here: window.location has already changed. any solution to "restore" the current location?
          return false;
        } else {
          const restoredState = state?.navigationId ? state : null;
          if (state) {
            const stateCopy = {
              ...state
            };
            delete stateCopy.navigationId;
            delete stateCopy.ɵrouterPageId;
            if (Object.keys(stateCopy).length !== 0) {
              extras.state = stateCopy;
            }
          }
          let urlTree = this.parseUrl(url);
          urlTree = this.maybeApplyRouterLinkRedirect(urlTree, {});
          this.scheduleNavigation(urlTree, source, restoredState, extras);
        }
      });
    }
    // this method is not in use currently, but its idea is too interesting to be thrown away.
    // left for whenever such a mechanism is needed.
    // by "hijacking" the "handleNavigationRequest" method of @angular/router-internal NavigationTransitions class,
    // it is possible to track full data of ANY router navigation at the moment of scheduling.
    trackAllNavigations() {
      const navigationTransitions = this.navigationTransitions;
      const originalMethod = navigationTransitions.handleNavigationRequest;
      navigationTransitions.handleNavigationRequest = request => {
        console.log('>>> handle nav request', request);
        originalMethod.call(navigationTransitions, request);
      };
    }
    waitForNavigationInterceptors(url, extras) {
      return new Promise((res, rej) => {
        if (this.navigationInterceptors.length === 0) {
          return res(true);
        }
        const promises = this.navigationInterceptors.map(interceptor => {
          return interceptor.intercept(url, extras);
        });
        Promise.all(promises).then(interceptorResults => {
          const anyBlocked = interceptorResults.some(r => r === false);
          res(!anyBlocked);
        });
      });
    }
    maybeApplyRouterLinkRedirect(urlTree, extras) {
      const redirectRule = this.redirects.findRedirect(urlTree);
      if (!redirectRule) {
        return urlTree;
      } else {
        const result = this.redirects.applyRedirect(urlTree, redirectRule);
        return Array.isArray(result) ? this.createUrlTree(result, extras) : result;
      }
    }
    createUrlTree(commands, navigationExtras = {}) {
      /**
       * angular router handles initial route different to dynamic route changes:
       * While an initial route path "/user/activate?id=XXX" can be parsed, calling router.navigate(['/user/activate?id=XXX']) will error.
       * This is because router.navigate expects queryParams to be passed in navigationExtras.
       * To fix this difference, below code will scan commands for queryParams and extracts them into navigationExtras.
       */
      const queryParamCmdIndex = commands.findIndex(cmd => typeof cmd === 'string' && cmd.includes('?'));
      if (queryParamCmdIndex > -1) {
        // extract queryParams from command
        const cmd = commands[queryParamCmdIndex];
        const params = parseUrlParams2(cmd, false);
        navigationExtras.queryParams = {
          ...params,
          ...(navigationExtras.queryParams || {})
        };
        // remove params from command segment
        if (cmd.startsWith('?')) {
          commands.splice(queryParamCmdIndex, 1);
        } else {
          commands[queryParamCmdIndex] = cmd.substring(0, cmd.indexOf('?'));
        }
      }
      const tree = super.createUrlTree(commands, {
        ...navigationExtras
      });
      // console.log('created urlTree',commands,tree);
      return tree;
    }
    /** resetConfig will be called whenever the available routes change (on startup too) */
    /** it will be called first from parent constructor, so BEFORE custom constructor executes! */
    resetConfig(routes) {
      // due to being called very early, this.customConfig will not be defined on first call.
      // fall back to inject it manually.
      if (!this.customConfig) this.customConfig = inject(CUSTOM_ROUTER_CONFIG);
      super.resetConfig(routes);
    }
    /** override isActive implementation to handle redirected links correctly */
    iisActive(url, matchOptions) {
      if (typeof url === 'string') {
        return isActive(url, this, matchOptions)();
      }
      const redirectRule = this.redirects.findRedirect(url);
      const compareToRedirectedRoute = redirectRule && redirectRule.type === RouterLinkRedirectType.Replace;
      if (!compareToRedirectedRoute) {
        return isActive(url, this, matchOptions)();
      } else {
        const result = this.redirects.applyRedirect(url, redirectRule);
        const redirectTree = Array.isArray(result) ? this.createUrlTree(result) : result;
        return isActive(redirectTree, this, matchOptions)();
      }
    }
    isPathActive(pathInput) {
      let path = Array.isArray(pathInput) ? pathInput.join('/') : pathInput;
      if (!path) throw new Error('isPathActive failed to check because passed path is null');
      if (!path.startsWith('/')) path = '/' + path;
      // this.url will not be correct after initial navigation!
      // Need to fall back to accessing window.location instead.
      if (this.isBrowser) {
        // const currentPath = this.url;
        const currentPath = window.location.pathname;
        const isMatching = currentPath.startsWith(path);
        return isMatching;
      } else {
        // not possible during SSR, use possibly incorrect url property instead.
        const currentPath = this.url;
        const isMatching = currentPath.startsWith(path);
        return isMatching;
      }
    }
    static {
      this.ɵfac = function CustomRouter_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CustomRouter)(i0.ɵɵinject(i0.Injector));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: CustomRouter,
        factory: CustomRouter.ɵfac
      });
    }
  }
  return CustomRouter;
})();